import { SelectableWithIdObject } from "src/components/Form/types";
import type { UserCrew, UserWithRolesResponse } from "../shared/api-types/user";

import request from "./request";

export interface CrewShortResponse {
  id: number;
  companyName: string;
}

interface CrewResponse extends CrewShortResponse {
  contactPerson: string;
  email: string;
  phone: string;
  status: string;
  address: string;
  postcode: string;
  city: string;
}

export class CrewShort extends SelectableWithIdObject {
  public companyName: string;

  constructor(data: CrewShortResponse) {
    super(data.id);
    this.companyName = data.companyName;
  }

  getDisplayName(): string {
    return this.companyName;
  }
}

export class Crew extends CrewShort {
  public contactPerson: string;
  public email: string;
  public phone: string;
  public status: string;
  public address: string;
  public postcode: string;
  public city: string;

  constructor(data: CrewResponse) {
    super(data);
    this.companyName = data.companyName;
    this.contactPerson = data.contactPerson;
    this.email = data.email;
    this.phone = data.phone;
    this.status = data.status;
    this.address = data.address;
    this.postcode = data.postcode;
    this.city = data.city;
  }
}

export interface Permission {
  id: number;
  code: string;
  crewId: number | null;
  name: string;
}

export interface Role {
  id: number;
  name: string;
  code?: string;
}

export interface CrewWithPermissions {
  id: number;
  name: string;
  status: string;
  permissions: Permission[];
  roles: Role[];
  admin: boolean;
  moodleCompanyCourses?: number;
}

export interface CrewBase {
  id: number;
  name: string;
}

/** Crew shape as enriched and stored in CrewContext */
export interface CrewContext extends CrewWithPermissions {
  globalId?: string;
  logo?: string;
  subscription?: string;
  bases?: CrewBase[];
  baseIds?: number[] | null;
  baseName?: string | null;
  [key: string]: unknown;
}

/** User shape as stored in UserContext */
export interface UserContext {
  id: number;
  fullname: string;
  email: string;
  username: string;
  sa: boolean;
  ta: boolean;
  crews: CrewContext[];
}

export const mapCrewShort = (data: CrewShortResponse): CrewShort => {
  return new CrewShort(data);
};

export const mapCrew = (data: CrewResponse): Crew => {
  return new Crew(data);
};

export const getCrews = async (): Promise<Crew[]> => {
  const response = await request(`/crew`);
  return response.map((data: CrewResponse) => mapCrew(data));
};

export const getCrewOperations = async (crewId: string) => {
  const json = await request(`/crew/${crewId}/operations`);
  return json;
};

export const getMembersList = async (
  crewId: string | number,
  baseIds?: number[] | null,
): Promise<UserWithRolesResponse> => {
  const params = new URLSearchParams();
  baseIds?.forEach((id) => params.append("baseIds", String(id)));
  const queryString = params.toString();
  const endpoint = `/crew/${crewId}/users${queryString ? `?${queryString}` : ""}`;
  return request(endpoint);
};

/**
 * Get crew users with their associated crews using the new /users/crew/:crewId endpoint
 * @param crewId - The crew ID
 * @returns Array of users with their crew associations
 */
export const getCrewUsers = async (crewId: number): Promise<UserCrew[]> => {
  const json = await request(`/user/crew/${crewId}`);
  return json;
};

export const getCrewRelations = async (crewId: string, userId?: number) => {
  const json = await request(`/relation/${crewId}${userId ? `/${userId}` : ""}`);
  return json;
};

interface AuthorizedUserResponse {
  id: number;
  fullname: string;
  email: string;
  roles: string[];
}

export class AuthorizedUser extends SelectableWithIdObject {
  public fullname: string;
  public email: string;
  public roles: string[];

  constructor(data: AuthorizedUserResponse) {
    super(data.id);
    this.fullname = data.fullname;
    this.email = data.email;
    this.roles = data.roles;
  }

  getDisplayName(): string {
    return `${this.fullname} (${this.roles.join(", ")})`;
  }
}

export const getAuthorizedUsers = async (crewId: string): Promise<AuthorizedUser[]> => {
  const json = await request(`/crew/${crewId}/company-course-admins`);
  return json.map((data: AuthorizedUserResponse) => new AuthorizedUser(data));
};
